<section class="generators-management-page">
  <div class="page-shell">
    <header class="page-header app-card">
      <div>
        <h1>{{ header().title }}</h1>
        <p>{{ header().description }}</p>
      </div>

      <div class="header-actions">
        <a class="secondary-link" [routerLink]="manageUnitsLink()">Manage Generation Units</a>
        <button type="button" class="primary-button" (click)="openCreateForm()">+ Add New</button>
      </div>
    </header>

    <div class="stats-grid" aria-label="Generator summary">
      @for (card of stats(); track card.label) {
        <article class="stat-card app-card" [class]="'tone-' + card.tone">
          <div class="stat-icon" aria-hidden="true">{{ card.icon }}</div>
          <div class="stat-copy">
            <span>{{ card.label }}</span>
            <strong>
              {{ card.value }}
              @if (card.suffix) {
                <small>{{ card.suffix }}</small>
              }
            </strong>
          </div>
        </article>
      }
    </div>

    <section class="table-card app-card">
      <div class="menus-card__filters">
      <label class="search-field">
        <span>Search Generator</span>
        <div class="input-with-icon">
          <span class="input-icon">⌕</span>
          <input
            type="search"
            placeholder="Enter generator code, name, org type..."
            [value]="searchTerm()"
            (input)="onSearch($any($event.target).value)"
          >
        </div>
      </label>
      </div>

      @if (toast()) {
        <div class="toast-message" [class.error]="toast()?.type === 'error'" role="status">
          {{ toast()?.message }}
        </div>
      }

      <div class="table-wrap">
        <table class="table-sm menu-table">
          <thead>
            <tr>
              <th>S.No.</th>
              <th>Generator Code</th>
              <th>Generator Name</th>
              <th>Organization Type</th>
              <th>Source Type</th>
              <th>Fuel Type</th>
              <th>No. of Units</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            @for (generator of generators(); track generator.id; let index = $index) {
              <tr>
                <td>{{ showingStart() + index }}</td>
                <td>{{ generator.generator_code }}</td>
                <td><div class="menu-name">{{ generator.name_of_power_plant }}</div></td>
                <td>{{ generator.organization_type }}</td>
                <td>{{ generator.source_type }}</td>
                <td>{{ generator.fuel_type || '-' }}</td>
                <td>
                  <button type="button" class="unit-pill" (click)="openUnitsModal(generator)">
                    {{ generator.unit_count }}
                  </button>
                </td>
                <td>
                  <div class="action-buttons">
                    <button type="button" class="action-button edit-button" (click)="edit(generator)" aria-label="Edit generator">✎</button>
                    <button type="button" class="action-button delete-button" (click)="remove(generator)" aria-label="Delete generator">🗑</button>
                  </div>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="8"><div class="empty-state">No generators found.</div></td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <footer class="table-footer">
        <span>Showing {{ showingStart() }} to {{ showingEnd() }} of {{ totalItems() }} entries</span>

        <div class="pagination">
          <button type="button" class="page-button" [disabled]="currentPage() === 1" (click)="goToPage(currentPage() - 1)">‹</button>
          @for (page of pageNumbers(); track page) {
            <button type="button" class="page-button" [class.active]="page === currentPage()" (click)="goToPage(page)">
              {{ page }}
            </button>
          }
          <button type="button" class="page-button" [disabled]="currentPage() === totalPages()" (click)="goToPage(currentPage() + 1)">›</button>
        </div>
      </footer>
    </section>
  </div>

  @if (isFormOpen()) {
    <div class="modal-backdrop" role="presentation">
      <section class="menu-modal menu-modal--wide" role="dialog" aria-modal="true" aria-labelledby="generator-modal-title">
        <div class="menu-modal__header">
          <div>
            <p>{{ editingGeneratorId() ? 'Edit Generator' : 'Add New Generator' }}</p>
            <h2 id="generator-modal-title">{{ editingGeneratorId() ? 'Update generator details' : 'Create a new generator' }}</h2>
          </div>

          <button type="button" class="modal-close" (click)="closeForm()">×</button>
        </div>

        <form class="menu-form" [formGroup]="form" (ngSubmit)="save()">
          <div class="form-grid">
            <label>
              <span>Organization Type</span>
              <input formControlName="organization_type" placeholder="Type">
            </label>

            <label>
              <span>Source Type</span>
              <input formControlName="source_type" placeholder="Type">
            </label>

            <label>
              <span>Fuel Type</span>
              <input formControlName="fuel_type" placeholder="Fuel type">
            </label>

            <label>
              <span>Name Of Power Plant</span>
              <input formControlName="name_of_power_plant" placeholder="Power plant name">
            </label>

            <label>
              <span>ID</span>
              <input formControlName="generator_code" placeholder="Generator code">
            </label>

            <label>
              <span>Parent Company</span>
              <input formControlName="parent_company" placeholder="Parent company">
            </label>

            <label>
              <span>SCADA Station Code</span>
              <input formControlName="scada_station_code" placeholder="SCADA code">
            </label>

            <label>
              <span>Connecting Substation</span>
              <input formControlName="connecting_substation" placeholder="Substation">
            </label>

            <label>
              <span>Connecting Voltage Level (kV)</span>
              <input formControlName="connecting_voltage_level_kv" placeholder="Voltage level">
            </label>

            <label>
              <span>Voltage Level (kV)</span>
              <input formControlName="voltage_level_kv" placeholder="Voltage level">
            </label>

            <label>
              <span>Installed Capacity (MW)</span>
              <input formControlName="installed_capacity_mw" placeholder="Installed capacity">
            </label>

            <label>
              <span>Generator Capacity (MW)</span>
              <input formControlName="generator_capacity_mw" placeholder="Generator capacity">
            </label>

            <label>
              <span>No of Units</span>
              <input type="number" formControlName="no_of_units" readonly>
              <small>Synced from linked units</small>
            </label>

            <label>
              <span>Log Sheet</span>
              <input formControlName="log_sheet" placeholder="Log sheet">
            </label>

            <label>
              <span>File</span>
              <input formControlName="file_path" placeholder="File path">
            </label>

            <label class="field--full">
              <span>Other Parameter</span>
              <input formControlName="other_parameter" placeholder="Other parameter">
            </label>
          </div>

          <div class="form-actions">
            <button type="button" class="secondary-button" (click)="closeForm()">Cancel</button>
            <button type="submit" class="primary-button" [disabled]="form.invalid">
              {{ editingGeneratorId() ? 'Update Generator' : 'Create Generator' }}
            </button>
          </div>
        </form>
      </section>
    </div>
  }

  @if (isUnitsModalOpen() && selectedGenerator()) {
    <div class="modal-backdrop" role="presentation">
      <section class="menu-modal menu-modal--units" role="dialog" aria-modal="true" aria-labelledby="generator-units-title">
        <div class="menu-modal__header">
          <div>
            <p>Generator Units</p>
            <h2 id="generator-units-title">{{ selectedGenerator()?.name_of_power_plant }}</h2>
            <span>{{ assignedUnits().length }} assigned units</span>
          </div>

          <button type="button" class="modal-close" (click)="closeUnitsModal()">×</button>
        </div>

        <div class="units-actions">
          <button type="button" class="primary-button" (click)="openAssignModal()">+ Add Unit</button>
        </div>

        <div class="units-table-wrap">
          <table class="table-sm">
            <thead>
              <tr>
                <th>Unique No</th>
                <th>Model</th>
                <th>Developer</th>
                <th>Capacity</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              @for (unit of assignedUnits(); track unit.id) {
                <tr>
                  <td>{{ unit.unique_no }}</td>
                  <td>{{ unit.model }}</td>
                  <td>{{ unit.name_of_developer }}</td>
                  <td>{{ unit.installed_capacity_mw }}</td>
                  <td>
                    <button type="button" class="danger-button" (click)="removeUnit(unit)">Remove</button>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="5">
                    <div class="empty-state">No units linked to this generator.</div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </section>
    </div>
  }

  @if (isAssignModalOpen() && selectedGenerator()) {
    <div class="modal-backdrop" role="presentation">
      <section class="menu-modal menu-modal--units" role="dialog" aria-modal="true" aria-labelledby="assign-units-title">
        <div class="menu-modal__header">
          <div>
            <p>Add Unit</p>
            <h2 id="assign-units-title">Assign available units</h2>
          </div>

          <button type="button" class="modal-close" (click)="closeAssignModal()">×</button>
        </div>

        <label class="search-field modal-search-field">
          <span>Search Unit</span>
          <div class="input-with-icon">
            <span class="input-icon">⌕</span>
            <input
              type="search"
              placeholder="Search unique no or model..."
              [value]="availableUnitSearch()"
              (input)="onAvailableUnitSearch($any($event.target).value)"
            >
          </div>
        </label>

        <div class="units-table-wrap">
          <table class="table-sm">
            <thead>
              <tr>
                <th>Unique No</th>
                <th>Model</th>
                <th>Developer</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              @for (unit of availableUnits(); track unit.id) {
                <tr>
                  <td>{{ unit.unique_no }}</td>
                  <td>{{ unit.model }}</td>
                  <td>{{ unit.name_of_developer }}</td>
                  <td>
                    <button type="button" class="primary-button" (click)="assignUnit(unit)">Add</button>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="4">
                    <div class="empty-state">No available units found.</div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </section>
    </div>
  }
</section>
